export interface DimensionsMeta {
  referenceWidth: number; // Original/source image width (where coordinates were created)
  referenceHeight: number; // Original/source image height
  displayedWidth: number; // Rendered image width on canvas
  displayedHeight: number; // Rendered image height on canvas
  offsetX: number; // Left letterbox offset
  offsetY: number; // Top letterbox offset
}

export interface Point {
  x: number;
  y: number;
}

interface CoordinateBalloon {
  coordinate_x?: number | null;
  coordinate_y?: number | null;
}

/** Fallback when the drawing file is missing and the API returns [0, 0]. */
export function inferPageDimensions(
  balloons: CoordinateBalloon[],
): { width: number; height: number } | null {
  if (!balloons.length) {
    return null;
  }

  let maxX = 0;
  let maxY = 0;
  for (const balloon of balloons) {
    maxX = Math.max(maxX, balloon.coordinate_x ?? 0);
    maxY = Math.max(maxY, balloon.coordinate_y ?? 0);
  }

  if (maxX <= 0 && maxY <= 0) {
    return null;
  }

  const padding = 250;
  return {
    width: Math.max(maxX + padding, 800),
    height: Math.max(maxY + padding, 600),
  };
}

export function resolveReferenceDimensions(
  apiWidth: number,
  apiHeight: number,
  naturalWidth: number,
  naturalHeight: number,
  balloons: CoordinateBalloon[],
): { width: number; height: number } {
  if (apiWidth > 0 && apiHeight > 0) {
    return { width: apiWidth, height: apiHeight };
  }
  if (naturalWidth > 0 && naturalHeight > 0) {
    return { width: naturalWidth, height: naturalHeight };
  }

  const inferred = inferPageDimensions(balloons);
  if (inferred) {
    return inferred;
  }

  return { width: 0, height: 0 };
}

// Map image (source) coordinates -> displayed canvas coordinates
export function imageToDisplayed(p: Point, m: DimensionsMeta): Point {
  return {
    x: m.offsetX + (p.x / m.referenceWidth) * m.displayedWidth,
    y: m.offsetY + (p.y / m.referenceHeight) * m.displayedHeight,
  };
}

// Map displayed canvas coordinates -> image (source) coordinates
export function displayedToImage(p: Point, m: DimensionsMeta): Point {
  return {
    x: ((p.x - m.offsetX) / m.displayedWidth) * m.referenceWidth,
    y: ((p.y - m.offsetY) / m.displayedHeight) * m.referenceHeight,
  };
}

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function boxToDisplayed(b: Box, m: DimensionsMeta): Box {
  const topLeft = imageToDisplayed({ x: b.x, y: b.y }, m);
  const bottomRight = imageToDisplayed({ x: b.x + b.w, y: b.y + b.h }, m);
  return {
    x: topLeft.x,
    y: topLeft.y,
    w: bottomRight.x - topLeft.x,
    h: bottomRight.y - topLeft.y,
  };
}

/** Closest point on the box outline to `p`, for anchoring a leader line. */
export function nearestPointOnBox(p: Point, b: Box): Point {
  const x = Math.min(Math.max(p.x, b.x), b.x + b.w);
  const y = Math.min(Math.max(p.y, b.y), b.y + b.h);
  const inside = x === p.x && y === p.y;
  if (!inside) {
    return { x, y };
  }
  const distances = [
    { d: p.x - b.x, pt: { x: b.x, y: p.y } },
    { d: b.x + b.w - p.x, pt: { x: b.x + b.w, y: p.y } },
    { d: p.y - b.y, pt: { x: p.x, y: b.y } },
    { d: b.y + b.h - p.y, pt: { x: p.x, y: b.y + b.h } },
  ];
  return distances.reduce((best, c) => (c.d < best.d ? c : best)).pt;
}

// Compute the scale for balloon size relative to source coordinates
export function letterboxScale(referenceWidth: number, displayedWidth: number): number {
  return referenceWidth ? displayedWidth / referenceWidth : 0;
}
